import { Box, Image, Stack, Text } from "@mantine/core";
import {
  createFileRoute,
  retainSearchParams,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import styles from "./work-order-accepted.module.scss";
import { Button, Card } from "@onengine/component-library";
import VisitInfoCard from "@/components/features/WorkOrders/AcceptWorkOrder/VisitInfoCard/VisitInfoCard";
import type { AcceptWorkOrderAssignedResponse } from "@/api/work-orders/acceptWorkOrder/acceptWorkOrder.mutations.types";

export const Route = createFileRoute(
  "/work-orders/$workOrderId/accept-work-order/work-order-accepted/",
)({
  component: WorkOrderAcceptedSucessfully,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
});

function WorkOrderAcceptedSucessfully() {
  const navigate = useNavigate();
  const { workOrderId } = Route.useParams();

  const acceptResponse = useRouterState({
    select: (state) =>
      (state.location.state as { acceptResponse?: AcceptWorkOrderAssignedResponse } | undefined)
        ?.acceptResponse,
  });

  const visitData = acceptResponse
    ? [
        {
          visitId: acceptResponse.workOrderNumber ?? "",
          visitNumber: `Visit #${acceptResponse.visit.visitNumber}`,
          workOrderId: acceptResponse.workOrderId,
          visitType: acceptResponse.visit.visitType.label,
          expectedOnsiteDate: acceptResponse.visit.scheduledTime,
          visitStatus: acceptResponse.visit.status.label,
        },
      ]
    : [];

  const handleReturn = () => {
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId: workOrderId },
      search: (prev) => ({ ...prev, tab: "overview" }),
    });
  };

  return (
    <Box className={styles.successContainer}>
      <Stack style={{ maxWidth: 600 }} gap={"var(--spacing-x-large, 24px)"}>
        <Stack>
          <Image style={{ width: "64px" }} src={"/favicon.svg"} alt="Success" />
          <Text className={styles.successHeaderText}>Work Order Accepted!</Text>
        </Stack>
        <Card style={{ padding: "var(--spacing-medium, 16px)" }}>
          <Text className={styles.successDescriptionText}>
            This work order is now confirmed and has been added to your schedule. You can review the
            details or make updates from the work order page.
          </Text>
        </Card>
        <Card style={{ padding: "var(--spacing-medium, 16px)" }}>
          <Stack gap={"var(--spacing-x-large, 24px)"}>
            <Text className={styles.successSubHeaderText}>Visit Information</Text>
            {visitData.map((visit) => {
              return <VisitInfoCard key={visit.visitId} visitData={visit} />;
            })}
          </Stack>
        </Card>
        <Button
          type="button"
          variant="primary"
          style={{ width: "fit-content", marginLeft: "auto" }}
          onClick={handleReturn}
        >
          Return to WO details
        </Button>
      </Stack>
    </Box>
  );
}
